import React, { useState, useEffect } from 'react';
import {
    Settings, Save, RefreshCw, Wifi, WifiOff, Zap, Hand,
    CheckCircle, XCircle, AlertCircle, Loader, Clock, Building2
} from 'lucide-react';
import Swal from 'sweetalert2';
import API_BASE_URL from '../config';
import ACCOUNTING_API_BASE from '../AccountingAPI';

const AccountingSystemSettings = () => {
    const [settings, setSettings] = useState({
        postingMode: 'manual',
        // ✅ Store the REAL accounting URL (shown in UI / saved to DB)
        // But test connection & posting always go through the PROXY
        accountingApiBaseUrl: 'https://delphys7-app.online:444/api',
        apiDescription: 'Delphys Accounting System',
        isActive: true,
    });

    const [originalSettings, setOriginalSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState(null); // null | 'online' | 'offline'
    const [lastSaved, setLastSaved] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    // ✅ FIXED: Compare using JSON.stringify so deep comparison works reliably
    useEffect(() => {
        if (originalSettings) {
            const changed = JSON.stringify(settings) !== JSON.stringify(originalSettings);
            setHasChanges(changed);
        } else {
            // No original settings yet (first load) — always allow save
            setHasChanges(true);
        }
    }, [settings, originalSettings]);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}api/AccountingSystemSettings`);
            if (res.ok) {
                const data = await res.json();
                const loaded = {
                    postingMode: data.postingMode || 'manual',
                    accountingApiBaseUrl: data.accountingApiBaseUrl || 'https://delphys7-app.online:444/api',
                    apiDescription: data.apiDescription || 'Delphys Accounting System',
                    isActive: data.isActive ?? true,
                };
                setSettings(loaded);
                setOriginalSettings(loaded);
                setLastSaved(data.updatedAt || null);
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const userSession = localStorage.getItem('user');
            const sessionData = userSession ? JSON.parse(userSession) : {};
            const updatedBy = sessionData.username || 'system';

            const res = await fetch(`${API_BASE_URL}api/AccountingSystemSettings`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...settings, updatedBy }),
            });

            if (!res.ok) throw new Error('Failed to save settings');

            const data = await res.json();
            setOriginalSettings(settings);
            setLastSaved(data.data?.updatedAt || new Date().toISOString());
            setHasChanges(false);

            await Swal.fire({
                icon: 'success',
                title: 'Settings Saved',
                text: 'Accounting system settings have been updated successfully.',
                confirmButtonColor: '#185FA5',
                timer: 2000,
                showConfirmButton: false,
            });
        } catch (error) {
            console.error('Error saving settings:', error);
            await Swal.fire({
                icon: 'error',
                title: 'Save Failed',
                text: error.message || 'An error occurred while saving.',
            });
        } finally {
            setSaving(false);
        }
    };

    // ✅ FIXED: Test connection goes through PRPO proxy — no direct browser→accounting call
    const handleTestConnection = async () => {
        setTesting(true);
        setConnectionStatus(null);
        try {
            // ACCOUNTING_API_BASE = "${API_BASE_URL}api/AccountingProxy"
            // So this hits: PRPO backend → accounting API (server-to-server, no CORS)
            const res = await fetch(`${ACCOUNTING_API_BASE}/PurchaseOrder`, {
                method: 'GET',
                signal: AbortSignal.timeout(10000),
            });

            if (res.ok || res.status === 401 || res.status === 403) {
                setConnectionStatus('online');
                await Swal.fire({
                    icon: 'success',
                    title: 'Connection Successful',
                    html: `Connected to <strong>${settings.accountingApiBaseUrl}</strong>`,
                    confirmButtonColor: '#185FA5',
                    timer: 2500,
                    showConfirmButton: false,
                });
            } else {
                throw new Error(`HTTP ${res.status}`);
            }
        } catch (error) {
            setConnectionStatus('offline');
            await Swal.fire({
                icon: 'error',
                title: 'Connection Failed',
                html: `Cannot reach <strong>${settings.accountingApiBaseUrl}</strong><br/><small>${error.message}</small>`,
            });
        } finally {
            setTesting(false);
        }
    };

    const handleReset = () => {
        if (originalSettings) {
            setSettings(originalSettings);
            setHasChanges(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        return new Date(dateString).toLocaleString('en-PH', {
            year: 'numeric', month: 'short', day: '2-digit',
            hour: '2-digit', minute: '2-digit',
        });
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center p-6">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mb-4"></div>
                    <p className="text-gray-500 text-sm">Loading settings...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen p-3 sm:p-6">
            <div className="max-w-3xl mx-auto">

                {/* ── Page Header ── */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-4 sm:p-6 shadow-lg mb-6">
                    <div className="flex items-start sm:items-center justify-between gap-3">
                        <div>
                            <h1 className="text-lg sm:text-2xl font-bold text-white flex items-center gap-2">
                                <Building2 className="w-5 h-5 sm:w-7 sm:h-7 shrink-0" />
                                Accounting System Settings
                            </h1>
                            <p className="text-blue-100 text-xs sm:text-sm mt-1">
                                Manage posting mode and external API connection
                            </p>
                        </div>
                        <div className="flex items-center gap-2 bg-white/20 px-3 py-2 rounded-lg shrink-0">
                            {connectionStatus === 'online' && <Wifi className="w-4 h-4 text-green-300" />}
                            {connectionStatus === 'offline' && <WifiOff className="w-4 h-4 text-red-300" />}
                            {connectionStatus === null && <Wifi className="w-4 h-4 text-white/60" />}
                            <span className="text-xs text-white font-medium">
                                {connectionStatus === 'online' ? 'Online' : connectionStatus === 'offline' ? 'Offline' : 'Not tested'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* ── Posting Mode Card ── */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4 overflow-hidden">
                    <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                        <Zap className="w-4 h-4 text-blue-600" />
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Posting Mode</span>
                    </div>
                    <div className="p-5">
                        <p className="text-sm text-gray-500 mb-4">
                            Choose how approved Purchase Orders are posted to the accounting system.
                        </p>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {/* Manual Option */}
                            <button
                                onClick={() => setSettings(s => ({ ...s, postingMode: 'manual' }))}
                                className={`relative flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${settings.postingMode === 'manual'
                                    ? 'border-blue-500 bg-blue-50'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                            >
                                <div className={`mt-0.5 w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${settings.postingMode === 'manual' ? 'bg-blue-100' : 'bg-gray-100'
                                    }`}>
                                    <Hand className={`w-5 h-5 ${settings.postingMode === 'manual' ? 'text-blue-600' : 'text-gray-400'}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <p className={`text-sm font-semibold ${settings.postingMode === 'manual' ? 'text-blue-900' : 'text-gray-700'}`}>
                                            Manual
                                        </p>
                                        {settings.postingMode === 'manual' && (
                                            <CheckCircle className="w-4 h-4 text-blue-600" />
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                                        A <strong>Post</strong> button appears on each approved PO. You control when to post.
                                    </p>
                                </div>
                            </button>

                            {/* Auto Option */}
                            <button
                                onClick={() => setSettings(s => ({ ...s, postingMode: 'auto' }))}
                                className={`relative flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${settings.postingMode === 'auto'
                                    ? 'border-green-500 bg-green-50'
                                    : 'border-gray-200 bg-white hover:border-gray-300'
                                    }`}
                            >
                                <div className={`mt-0.5 w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${settings.postingMode === 'auto' ? 'bg-green-100' : 'bg-gray-100'
                                    }`}>
                                    <Zap className={`w-5 h-5 ${settings.postingMode === 'auto' ? 'text-green-600' : 'text-gray-400'}`} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <p className={`text-sm font-semibold ${settings.postingMode === 'auto' ? 'text-green-900' : 'text-gray-700'}`}>
                                            Automatic
                                        </p>
                                        {settings.postingMode === 'auto' && (
                                            <CheckCircle className="w-4 h-4 text-green-600" />
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">
                                        POs are automatically posted to accounting immediately after approval.
                                    </p>
                                </div>
                            </button>
                        </div>

                        {/* Active mode info banner */}
                        {settings.postingMode === 'auto' && (
                            <div className="mt-4 flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                                <Zap className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                                <p className="text-xs text-green-800">
                                    <strong>Auto mode is on.</strong> Every approved PO will be instantly posted via the proxy to{' '}
                                    <span className="font-mono">{settings.accountingApiBaseUrl}/PurchaseOrder/bulk</span> — no manual action needed.
                                </p>
                            </div>
                        )}
                        {settings.postingMode === 'manual' && (
                            <div className="mt-4 flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                <Hand className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                                <p className="text-xs text-amber-800">
                                    <strong>Manual mode is on.</strong> A <strong>Post</strong> button will appear on each Approved PO row in the Purchase Order Approval page.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── API Connection Card ── */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4 overflow-hidden">
                    <div className="px-5 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-2">
                        <Settings className="w-4 h-4 text-blue-600" />
                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide">API Connection</span>
                    </div>
                    <div className="p-5 space-y-4">

                        {/* Base URL */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                                Accounting API Base URL <span className="text-red-500">*</span>
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    value={settings.accountingApiBaseUrl}
                                    onChange={(e) => setSettings(s => ({ ...s, accountingApiBaseUrl: e.target.value }))}
                                    placeholder="https://example.com:444/api"
                                    className="w-full px-4 py-2.5 pr-24 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400 bg-gray-50"
                                />
                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(settings.accountingApiBaseUrl);
                                        Swal.fire({ icon: 'success', title: 'Copied!', timer: 1000, showConfirmButton: false });
                                    }}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 px-2.5 py-1 bg-gray-200 hover:bg-gray-300 text-gray-600 text-xs rounded-md transition-colors"
                                >
                                    Copy
                                </button>
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                                Requests are routed via: <span className="font-mono text-blue-600">{ACCOUNTING_API_BASE}</span>
                            </p>
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                                System Description
                            </label>
                            <input
                                type="text"
                                value={settings.apiDescription}
                                onChange={(e) => setSettings(s => ({ ...s, apiDescription: e.target.value }))}
                                placeholder="e.g. Delphys Accounting System"
                                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                            />
                        </div>

                        {/* Active toggle */}
                        <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                            <div>
                                <p className="text-sm font-semibold text-gray-700">Connection Active</p>
                                <p className="text-xs text-gray-500 mt-0.5">Disable to pause all posting to accounting</p>
                            </div>
                            <button
                                onClick={() => setSettings(s => ({ ...s, isActive: !s.isActive }))}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${settings.isActive ? 'bg-blue-600' : 'bg-gray-300'
                                    }`}
                            >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.isActive ? 'translate-x-6' : 'translate-x-1'
                                    }`} />
                            </button>
                        </div>

                        {!settings.isActive && (
                            <div className="flex items-start gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                                <p className="text-xs text-red-700">
                                    <strong>Connection is disabled.</strong> No POs will be posted to accounting until this is re-enabled.
                                </p>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Last Saved Info ── */}
                {lastSaved && (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-4 px-5 py-4">
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                            <Clock className="w-3.5 h-3.5" />
                            <span>Last saved: <span className="text-gray-700 font-medium">{formatDate(lastSaved)}</span></span>
                        </div>
                    </div>
                )}

                {/* ── Unsaved Changes Banner ── */}
                {hasChanges && (
                    <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-300 rounded-xl mb-4">
                        <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                        <p className="text-xs text-amber-800 flex-1">You have unsaved changes.</p>
                        <button onClick={handleReset} className="text-xs text-amber-700 underline hover:text-amber-900">
                            Discard
                        </button>
                    </div>
                )}

                {/* ── Action Buttons ── */}
                <div className="flex flex-col sm:flex-row gap-3">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm transition-all shadow-md ${saving
                            ? 'bg-gray-300 text-gray-500 cursor-not-allowed shadow-none'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                            }`}
                    >
                        {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                        {saving ? 'Saving...' : 'Save Settings'}
                    </button>

                    <button
                        onClick={handleTestConnection}
                        disabled={testing}
                        className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 transition-all"
                    >
                        {testing ? <Loader className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
                        {testing ? 'Testing...' : 'Test Connection'}
                    </button>

                    <button
                        onClick={fetchSettings}
                        className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 transition-all"
                        title="Refresh settings from server"
                    >
                        <RefreshCw className="w-4 h-4" />
                        Refresh
                    </button>
                </div>

            </div>
        </div>
    );
};

export default AccountingSystemSettings;